import { FC, useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';

import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import styles from './StanTipTapEditor.module.css';
import { StanTipTapEditorToolbar } from './Toolbar/StanTipTapEditorToolbar';
import { AISuggestionBlock } from './AISuggestionBlock/AISuggestionBlock';
import Iframe from './CustomExtensions/iframe';
import VariableItem from './CustomExtensions/variable-item';
import { cn } from '@/lib/utils/cn';
import { useAiGenerate } from './hooks/useAiGenerate';
import { EditorActions } from './editor.logic';
import { useEditorState } from './hooks/useEditorState';

import { ToolbarButtonType } from './types';

export interface StanTipTapEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    error?: boolean;
    disabled?: boolean;
    imageUploadAllowed?: boolean;
    linkAllowed?: boolean;
    videoAllowed?: boolean;
    hideToolbar?: boolean;
    generateAiAllowed?: boolean;
    personalizeItemData?: { name: string; id: string; required: boolean }[];
    allowAddProductLink?: boolean;
    onAiGenerate?: (prompt: string) => Promise<string>;
    placeholder?: string;
    visibleButtons?: ToolbarButtonType[];
}

export const StanTipTapEditor: FC<StanTipTapEditorProps> = ({
    value = '',
    onChange,
    className = '',
    error = false,
    disabled = false,
    imageUploadAllowed = true,
    linkAllowed = true,
    videoAllowed = true,
    hideToolbar = false,
    generateAiAllowed = true,
    personalizeItemData = [],
    allowAddProductLink = true,
    onAiGenerate,
    placeholder,
    visibleButtons,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false,
                heading: {
                    levels: [3],
                },
            }),
            Link.configure({ openOnClick: false, linkOnPaste: true }),
            Image.configure({ inline: true }),
            Placeholder.configure({
                placeholder: placeholder || 'Start typing...',
            }),
            Iframe,
            VariableItem,
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
    });

    // Sync editor content when value prop changes externally
    useEffect(() => {
        if (editor && value !== undefined) {
            const currentContent = editor.getHTML();
            // Only update if the content is actually different to avoid unnecessary updates
            if (currentContent !== value) {
                editor.commands.setContent(value, { emitUpdate: false });
            }
        }
    }, [editor, value]);

    const actions = useMemo(() => new EditorActions(editor), [editor]);
    const editorState = useEditorState(editor);
    const {
        aiSuggestionState,
        aiSuggestion,
        handleAiGenerate,
        handleAiSuggestionAccept,
        handleAiSuggestionDismiss,
        handleAiSuggestionRetry,
    } = useAiGenerate({ editorActions: actions, onAiGenerate });

    return (
        <>
            <div className={cn(styles.tipTapContainer, className, error && styles.error, disabled && styles.disabled)}>
                {!hideToolbar && (
                    <StanTipTapEditorToolbar
                        editor={editor}
                        error={error}
                        disabled={disabled}
                        imageUploadAllowed={imageUploadAllowed}
                        linkAllowed={linkAllowed}
                        videoAllowed={videoAllowed}
                        generateAiAllowed={generateAiAllowed}
                        personalizeItemData={personalizeItemData}
                        allowAddProductLink={allowAddProductLink}
                        onAiGenerate={handleAiGenerate}
                        isAiGenerating={aiSuggestionState === 'loading'}
                        visibleButtons={visibleButtons}
                    />
                )}
                <EditorContent editor={editor} />

                <BubbleMenu editor={editor} className={styles['bubble-menu']}>
                    <button
                        type='button'
                        onClick={actions.toggleBold}
                        className={editorState.isBold ? styles['is-active'] : ''}
                    >
                        Bold
                    </button>
                    <button
                        type='button'
                        onClick={actions.toggleItalic}
                        className={editorState.isItalic ? styles['is-active'] : ''}
                    >
                        Italic
                    </button>
                    <button
                        type='button'
                        onClick={actions.toggleStrike}
                        className={editorState.isStrike ? styles['is-active'] : ''}
                    >
                        Strike
                    </button>
                </BubbleMenu>
            </div>

            {aiSuggestionState !== 'idle' && (
                <AISuggestionBlock
                    state={aiSuggestionState}
                    suggestion={aiSuggestion}
                    onAccept={handleAiSuggestionAccept}
                    onDismiss={handleAiSuggestionDismiss}
                    onRetry={handleAiSuggestionRetry}
                />
            )}
        </>
    );
};
